/**
 * FrameCanvas -- Dual-layer canvas for frame display + mask/interaction overlay.
 *
 * Layer 1 (bottom): Renders the current frame ImageBitmap via drawImage.
 * Layer 2 (top): Transparent overlay for masks, prompt points, ROI drawing.
 *
 * Replaces the HTML5 <video> element + canvas overlay approach.
 */
import { useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import Box from '@mui/material/Box';

const FrameCanvas = forwardRef(function FrameCanvas(
  {
    frameBitmap,
    frameSize,
    onCanvasClick,
    onCanvasMouseMove,
    onCanvasMouseDown,
    onCanvasMouseUp,
    onCanvasWheel,
    style,
    overlayRef: externalOverlayRef,
    cursorStyle = 'crosshair',
  },
  ref,
) {
  const containerRef = useRef(null);
  const frameCanvasRef = useRef(null);
  const overlayCanvasRef = useRef(null);

  // Expose overlay canvas ref and frame canvas ref to parent
  useImperativeHandle(ref, () => ({
    get overlayCanvas() {
      return overlayCanvasRef.current;
    },
    get frameCanvas() {
      return frameCanvasRef.current;
    },
    get container() {
      return containerRef.current;
    },
    getOverlayContext() {
      return overlayCanvasRef.current?.getContext('2d');
    },
    clearOverlay() {
      const canvas = overlayCanvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    },
  }));

  // Sync external overlay ref if provided
  useEffect(() => {
    if (externalOverlayRef) {
      externalOverlayRef.current = overlayCanvasRef.current;
    }
  });

  // Draw the frame bitmap when it changes
  useEffect(() => {
    const canvas = frameCanvasRef.current;
    if (!canvas || !frameBitmap) return;

    // Set canvas to native frame resolution
    if (canvas.width !== frameBitmap.width || canvas.height !== frameBitmap.height) {
      canvas.width = frameBitmap.width;
      canvas.height = frameBitmap.height;
      // Match overlay canvas dimensions
      const overlay = overlayCanvasRef.current;
      if (overlay) {
        overlay.width = frameBitmap.width;
        overlay.height = frameBitmap.height;
      }
    }

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    try {
      ctx.drawImage(frameBitmap, 0, 0);
    } catch (e) {
      // ImageBitmap can be detached if the cache evicted it between when
      // React captured the prop and when the effect ran. Skip; the next
      // setCurrentBitmap will trigger a fresh paint.
      if (e && e.name !== 'InvalidStateError') throw e;
    }
  }, [frameBitmap]);

  // Set canvas dimensions from frameSize if no bitmap yet
  useEffect(() => {
    if (frameBitmap) return; // bitmap handler takes care of sizing
    if (!frameSize?.width) return;
    const canvas = frameCanvasRef.current;
    const overlay = overlayCanvasRef.current;
    if (canvas && canvas.width !== frameSize.width) {
      canvas.width = frameSize.width;
      canvas.height = frameSize.height;
    }
    if (overlay && overlay.width !== frameSize.width) {
      overlay.width = frameSize.width;
      overlay.height = frameSize.height;
    }
  }, [frameSize, frameBitmap]);

  // Compute the actual rendered content area for objectFit: contain.
  // The canvas element has CSS width/height = 100% of container, but
  // objectFit: contain letterboxes the content. We need to find the
  // pixel offset and scale of the content within the bounding rect.
  const getContainedRect = useCallback((canvas) => {
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const cw = canvas.width;
    const ch = canvas.height;
    if (!cw || !ch) return null;

    const containerAspect = rect.width / rect.height;
    const canvasAspect = cw / ch;

    let renderW, renderH, offsetX, offsetY;
    if (canvasAspect > containerAspect) {
      // Canvas is wider than container -- fit by width, letterbox top/bottom
      renderW = rect.width;
      renderH = rect.width / canvasAspect;
      offsetX = 0;
      offsetY = (rect.height - renderH) / 2;
    } else {
      // Canvas is taller than container -- fit by height, letterbox left/right
      renderH = rect.height;
      renderW = rect.height * canvasAspect;
      offsetX = (rect.width - renderW) / 2;
      offsetY = 0;
    }

    return { renderW, renderH, offsetX, offsetY, rect };
  }, []);

  // Convert mouse event to canvas-space coordinates, accounting for objectFit: contain
  const toCanvasCoords = useCallback(
    (e) => {
      const canvas = overlayCanvasRef.current;
      if (!canvas) return null;

      const contained = getContainedRect(canvas);
      if (!contained) return null;

      const { renderW, renderH, offsetX, offsetY, rect } = contained;

      // Mouse position relative to the rendered content area
      const localX = e.clientX - rect.left - offsetX;
      const localY = e.clientY - rect.top - offsetY;

      // Scale to canvas internal resolution
      const scaleX = canvas.width / renderW;
      const scaleY = canvas.height / renderH;

      return {
        x: localX * scaleX,
        y: localY * scaleY,
      };
    },
    [getContainedRect],
  );

  const handleClick = useCallback(
    (e) => {
      if (!onCanvasClick) return;
      const coords = toCanvasCoords(e);
      if (coords) onCanvasClick(coords, e);
    },
    [onCanvasClick, toCanvasCoords],
  );

  const handleMouseMove = useCallback(
    (e) => {
      if (!onCanvasMouseMove) return;
      const coords = toCanvasCoords(e);
      if (coords) onCanvasMouseMove(coords, e);
    },
    [onCanvasMouseMove, toCanvasCoords],
  );

  const handleMouseDown = useCallback(
    (e) => {
      if (!onCanvasMouseDown) return;
      const coords = toCanvasCoords(e);
      if (coords) onCanvasMouseDown(coords, e);
    },
    [onCanvasMouseDown, toCanvasCoords],
  );

  const handleMouseUp = useCallback(
    (e) => {
      if (!onCanvasMouseUp) return;
      const coords = toCanvasCoords(e);
      if (coords) onCanvasMouseUp(coords, e);
    },
    [onCanvasMouseUp, toCanvasCoords],
  );

  const handleWheel = useCallback(
    (e) => {
      if (!onCanvasWheel) return;
      e.preventDefault();
      const coords = toCanvasCoords(e);
      if (coords) onCanvasWheel(coords, e);
    },
    [onCanvasWheel, toCanvasCoords],
  );

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        backgroundColor: '#111',
        ...style,
      }}
    >
      {/* Frame layer -- no pointer events */}
      <canvas
        ref={frameCanvasRef}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          display: 'block',
        }}
      />
      {/* Overlay layer -- receives all pointer events */}
      <canvas
        ref={overlayCanvasRef}
        onClick={handleClick}
        onMouseMove={handleMouseMove}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onWheel={handleWheel}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          cursor: cursorStyle,
          pointerEvents: 'auto',
        }}
      />
      {/* Loading placeholder when no frame is loaded */}
      {!frameBitmap && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#555',
            fontSize: 14,
          }}
        >
          Loading frame...
        </Box>
      )}
    </Box>
  );
});

export default FrameCanvas;
